import yaml from 'js-yaml';
import { useCallback } from 'react';
import { TEMPLATE_OS_CONFIG, useToast } from '@bymason-private/controller-shared-widgets';
import { ControllerFontType } from '@bymason-private/controller-v1-theme-provider';
import { Box, Link, Text } from '@bymason-private/legacy-pattern-components';
import { ConfigModel } from '@bymason/controller-api-hooks';
import { downloadFile } from '@bymason/utils';
import { YamlConfigDisplay } from '../../../../common/yaml-display';
import SelectArtifact from './select-artifact';

export default function ConfigStep({ projectName, config, onConfigChange }: ConfigStepProps) {
  const [setToastMessage, setToastSeverity] = useToast();

  const handleError = (message: string) => {
    setToastMessage(message);
    setToastSeverity('error');
  };

  const handleLoadConfig = useCallback(
    (newConfig: ConfigModel) => {
      onConfigChange(newConfig);
    },
    [onConfigChange]
  );

  const handleDownloadTemplate = useCallback(() => {
    const tempConfig = {
      ...TEMPLATE_OS_CONFIG,
      apps: config.apps,
      media: config.media,
      os: { ...TEMPLATE_OS_CONFIG.os, ...config.os },
    };
    const yamlConfig = yaml.dump(tempConfig, { lineWidth: -1 });
    downloadFile({ source: yamlConfig, fileName: `${projectName}.yml`, type: 'text' });
  }, [projectName, config]);

  return (
    <div>
      <SelectArtifact
        header={
          <div>
            <Text variant={ControllerFontType.Heading300B}>Select your configuration file (yaml).{` `}</Text>
            <Text variant={ControllerFontType.Body400R}>
              An OS configuration describes the behavior of the device system software. This includes OS
              features, applications, device settings etc. Visit the{' '}
              <a
                href='https://docs.bymason.com/how-to/manage-projects#create-a-custom-mason-os-config'
                target='_blank'
                rel='noreferrer'
              >
                configuration guide
              </a>{' '}
              for more information.
            </Text>
            <Box sx={{ marginTop: 1 }}>
              <Link variant={ControllerFontType.Body400R} onClick={handleDownloadTemplate}>
                Download a YAML template for this build
              </Link>
            </Box>
          </div>
        }
        onLoadConfig={handleLoadConfig}
        onError={handleError}
        selectionType='config'
        showDropzone={!config.os.configurations}
      >
        <YamlConfigDisplay title={`Mason OS Config for project ${config?.os.name}`} configJson={config} />
      </SelectArtifact>
    </div>
  );
}

interface ConfigStepProps {
  projectName: string;
  config: ConfigModel;
  onConfigChange: (config: ConfigModel) => void;
}
